/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// src/components/Dashboard/Dashboard.tsx
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { ExportImportModal } from './ExportImportModal';
import { ASTUTOImportModal } from './ASTUTOImportModal';
import { ChecklistPreIntervento } from '../Checklist/ChecklistPreIntervento';
import { ReportFinale } from '../Report/ReportFinale';
import { QRCambioDOS } from '../QR/QRCambioDOS';
import { HandoverModal } from './HandoverModal';
import './Dashboard.css';

interface DashboardProps {
  onNavigate: (target: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const [showExportModal, setShowExportModal] = useState(false);
  const [showASTUTOModal, setShowASTUTOModal] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showHandover, setShowHandover] = useState(false);
  
  // LEGGI DATI DALLO STORE
  const personale = useAppStore(state => state.personale);
  const mezziTerrestri = useAppStore(state => state.mezziTerrestri);
  const mezziAerei = useAppStore(state => state.mezziAerei);
  const droni = useAppStore(state => state.droni);
  const coau = useAppStore(state => state.coau);

  // 🔥 FOCOLAI ATTIVI: Come in app.js linea 809
  // Se COAU ha comune compilato → 1 incendio attivo, altrimenti 0
  const focolaiAttivi = coau?.comune ? 1 : 0;

  // CONTA MEZZI AEREI PER TIPO
  const alaFissa = mezziAerei.filter(m => m.tipo === 'ala_fissa').length;
  const alaRotante = mezziAerei.filter(m => m.tipo === 'ala_rotante').length;

  // FASE OPERATIVA
  const faseOperativa = coau?.comune
    ? 'IN CORSO'
    : 'IN ATTESA';
  const faseColor   = faseOperativa === 'IN CORSO' ? '#ef4444' : '#64748b';
  const faseBg      = faseOperativa === 'IN CORSO' ? 'rgba(239,68,68,0.12)' : 'rgba(100,116,139,0.08)';
  const faseDot     = faseOperativa === 'IN CORSO' ? '#ef4444' : '#64748b';

  // SEMAFORO per ogni risorsa
  const semaforoPersonale = personale.length === 0 ? 'red' : personale.length < 3 ? 'yellow' : 'green';
  const semaforoAerei     = mezziAerei.length === 0 ? 'yellow' : 'green';
  const semaforoTerra     = mezziTerrestri.length === 0 ? 'yellow' : 'green';

  const dotColor = (s: 'red'|'yellow'|'green') =>
    s === 'green' ? '#22c55e' : s === 'yellow' ? '#eab308' : '#ef4444';

  // STATISTICHE
  const stats = [
    { label: 'Operatori',      value: personale.length,      icon: '👥', color: '#3498db', semaforo: semaforoPersonale, onClick: () => onNavigate('risorse') },
    { label: 'Mezzi Terrestri', value: mezziTerrestri.length, icon: '🚛', color: '#e74c3c', semaforo: semaforoTerra,     onClick: () => onNavigate('risorse') },
    { label: 'Ala Fissa',       value: alaFissa,              icon: '✈️', color: '#3498db', semaforo: semaforoAerei,    onClick: () => onNavigate('risorse') },
    { label: 'Ala Rotante',     value: alaRotante,            icon: '🚁', color: '#f1c40f', semaforo: semaforoAerei,    onClick: () => onNavigate('risorse') },
    { label: 'Droni',           value: droni.length,          icon: '🛸', color: '#9b59b6', semaforo: 'green' as const, onClick: () => onNavigate('risorse') },
    { label: 'Focolai Attivi',  value: focolaiAttivi,         icon: '🔥', color: '#e67e22', semaforo: focolaiAttivi > 0 ? 'red' : 'green' as const, onClick: () => onNavigate('coau') },
  ] as { label: string; value: number; icon: string; color: string; semaforo: 'red'|'yellow'|'green'; onClick: () => void }[];

  return (
    <div className="dashboard-wrapper">

      {/* ── BANNER STATO OPERATIVO ── */}
      <div className="op-status-bar" style={{ background: faseBg, borderColor: faseColor }}>
        <div className="op-status-left">
          <span className="op-dot" style={{ background: faseDot, boxShadow: faseOperativa === 'IN CORSO' ? `0 0 8px ${faseDot}` : 'none' }} />
          <span className="op-fase" style={{ color: faseColor }}>{faseOperativa}</span>
          {coau?.comune && (
            <span className="op-location">
              📍 {[(coau as any).localita, coau.comune, coau.provincia].filter(Boolean).join(' — ')}
            </span>
          )}
          {coau?.oraAccertamento && (
            <span className="op-time">⏱ {coau.oraAccertamento}</span>
          )}
        </div>
        <div className="op-status-right">
          <span className="op-resource-badge" style={{ color: dotColor(semaforoPersonale) }}>
            👥 {personale.length}
          </span>
          <span className="op-resource-badge" style={{ color: dotColor(semaforoAerei) }}>
            ✈️ {alaFissa} / 🚁 {alaRotante}
          </span>
          <span className="op-resource-badge" style={{ color: focolaiAttivi > 0 ? '#ef4444' : '#22c55e' }}>
            🔥 {focolaiAttivi}
          </span>
        </div>
      </div>

      <div className="stats-grid">
        {stats.map((s, i) => (
          <div
            key={i}
            className="stat-card"
            onClick={s.onClick}
            style={{ cursor: 'pointer' }}
            title={`Clicca per gestire ${s.label}`}
          >
            <div className="stat-semaforo" style={{ background: dotColor(s.semaforo), boxShadow: `0 0 6px ${dotColor(s.semaforo)}` }} />
            <div className="stat-icon" style={{ color: s.color }}>{s.icon}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Handover Checklist */}
      <div className="handover-container" style={{ marginTop: '12px' }}>
        <button
          className="btn-handover-large"
          onClick={() => setShowHandover(true)}
          style={{
            background: 'linear-gradient(135deg, #1a2540 0%, #1e3a5f 100%)',
            borderColor: '#38bdf8',
          }}
        >
          <span className="icon">🔁</span>
          <div className="text-container">
            <span className="main-text">CHECKLIST PASSAGGIO CONSEGNE</span>
            <span className="sub-text">Stato risorse · Verifica COAU · Semafori operativi</span>
          </div>
        </button>
      </div>

      <div className="handover-container" style={{ marginTop: '12px' }}>
        <button
          className="btn-handover-large"
          onClick={() => setShowChecklist(true)}
          style={{
            background: 'linear-gradient(135deg, #166534 0%, #15803d 100%)',
            borderColor: '#22c55e',
          }}
        >
          <span className="icon">✅</span>
          <div className="text-container">
            <span className="main-text">CHECKLIST PRE-INTERVENTO</span>
            <span className="sub-text">L.A.C.E.S. · Sicurezza · Coordinamento COAU · Firma DOS</span>
          </div>
        </button>
      </div>

      <div className="handover-container" style={{ marginTop: '12px' }}>
        <button
          className="btn-handover-large"
          onClick={() => setShowQR(true)}
          style={{
            background: 'linear-gradient(135deg, #4a044e 0%, #86198f 100%)',
            borderColor: '#d946ef',
          }}
        >
          <span className="icon">📲</span>
          <div className="text-container">
            <span className="main-text">QR CODE — CAMBIO DOS</span>
            <span className="sub-text">Passaggio consegna istantaneo via scansione tablet</span>
          </div>
        </button>
      </div>

      <div className="handover-container">
        <button className="btn-handover-large" onClick={() => setShowExportModal(true)}>
          <span className="icon">🔄</span>
          <div className="text-container">
            <span className="main-text">PASSAGGIO CONSEGNA DOS</span>
            <span className="sub-text">Export/Import operazione completa con Media</span>
          </div>
        </button>
      </div>

      <div className="handover-container" style={{ marginTop: '12px' }}>
        <button
          className="btn-handover-large"
          onClick={() => setShowReport(true)}
          style={{
            background: 'linear-gradient(135deg, #1c1917 0%, #44403c 100%)',
            borderColor: '#a8a29e',
          }}
        >
          <span className="icon">📄</span>
          <div className="text-container">
            <span className="main-text">REPORT FINALE INTERVENTO</span>
            <span className="sub-text">Verbale PDF — COAU · Risorse · Diario · Firme</span>
          </div>
        </button>
      </div>

      <div className="handover-container" style={{ marginTop: '12px' }}>
        <button
          className="btn-handover-large"
          onClick={() => setShowASTUTOModal(true)}
          style={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%)',
            borderColor: '#2563eb',
          }}
        >
          <span className="icon">🌲</span>
          <div className="text-container">
            <span className="main-text">IMPORTA DA ASTUTO</span>
            <span className="sub-text">Personale, mezzi e coordinate da CFRS Sicilia</span>
          </div>
        </button>
      </div>

      {/* Modal Export/Import */}
      {showExportModal && (
        <ExportImportModal onClose={() => setShowExportModal(false)} />
      )}

      {/* Modal ASTUTO Import */}
      {showASTUTOModal && (
        <ASTUTOImportModal onClose={() => setShowASTUTOModal(false)} />
      )}

      {/* Checklist Pre-Intervento */}
      {showChecklist && (
        <ChecklistPreIntervento onClose={() => setShowChecklist(false)} />
      )}

      {/* Report Finale */}
      {showReport && (
        <ReportFinale onClose={() => setShowReport(false)} />
      )}

      {/* QR Cambio DOS */}
      {showQR && (
        <QRCambioDOS onClose={() => setShowQR(false)} />
      )}

      {/* Handover Checklist */}
      {showHandover && (
        <HandoverModal onClose={() => setShowHandover(false)} />
      )}
    </div>
  );
};
